import { Card, SideDrawer, withStyles } from 'vopay';
import { FC } from 'react';
import { CreateTicketPayload } from '../Tickets/types';
import { Button } from 'UI';
import { useClientLogin } from '@/providers/ClientLogin.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useTranslation } from 'react-i18next';
import useSupportTicket from '@/hooks/useApi/useSupportTicket';
import { useDataFetchIndicator } from '@/hooks';
import { useCreateTicketConfig } from './useCreateTicketConfig';
import styles from './CreateTicketDrawer.module.scss';

const cn = withStyles(styles);

type CreateTicketDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

const CreateTicketDrawer: FC<CreateTicketDrawerProps> = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'CreateTicketDrawer' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { submitTicket } = useSupportTicket();
  const [, setPending] = useDataFetchIndicator();
  const alertDialog = useAlertDialog();

  const { ctx, renderFormElements, detailsRows, frequencyRows } = useCreateTicketConfig({
    onSubmit: handleSubmit,
  });

  const {
    response: { LoginUserName },
  } = useClientLogin();

  async function handleSubmit(values: CreateTicketPayload) {
    const ticketData = {
      ...values,
      TicketRequesterName: LoginUserName,
    };

    try {
      setPending(true);
      const res = await submitTicket(ticketData);

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      setPending(false);

      await alertDialog.success(
        t('Dialogs.TicketCreationSuccess.title'),
        t('Dialogs.TicketCreationSuccess.description'),
        {
          confirmLabel: t('Dialogs.TicketCreationSuccess.confirmButton'),
        },
      );

      onSuccess();
      ctx.resetForm();
    } catch (err) {
      console.error(err);
      const desc = err instanceof Error ? err.message : t('Dialogs.TicketCreationError.description');

      const result = await alertDialog.error(t('Dialogs.TicketCreationError.title'), desc, {
        abortLabel: t('Dialogs.TicketCreationError.cancelButton'),
        confirmLabel: t('Dialogs.TicketCreationError.retryButton'),
      });

      if (result === 'confirm') {
        await handleSubmit(ticketData);
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={() => {
        ctx.resetForm();
        onClose();
      }}
      title={t('title')}
      footer={
        <Button
          colorScheme="primary"
          form="create-ticket"
          type="submit"
          style={{ width: '100%' }}
          disabled={!ctx.isValid}
        >
          {tGlobal('Buttons.submit')}
        </Button>
      }
    >
      <form id="create-ticket" onSubmit={ctx.handleSubmit} className={cn('flexCol', 'createTicketDrawerForm')}>
        <Card title={t('ticketDetailsSection')}>
          <div className={cn('flexCol', 'card')}>{renderFormElements({ config: detailsRows })}</div>
        </Card>

        <Card title={t('additionalDetailsSection')}>
          <div className={cn('flexCol', 'card')}>{renderFormElements({ config: frequencyRows })}</div>
        </Card>
      </form>
    </SideDrawer>
  );
};

export default CreateTicketDrawer;
